<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { authMixin, deviceMixin } from "@/mixins";
import { API_ENDPOINTS, PAGE_OPTIONS } from "@/constants";
import { requireAuth } from "@/utils/auth";
import { apiGet, apiPost } from "@/utils/request";
import { formatDateTime } from "@/utils/format";

/**
 * DNS记录列表页面（优化版）
 */
export default {
  page() {
    return {
      title: this.$t("menuitems.dns.record"),
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  mixins: [authMixin, deviceMixin],
  data() {
    return {
      title: "",
      items: [],
      loading: true,
      domain: "",
      table_columns: [
        {
          prop: "name",
          label: this.$t("dns.record.host"),
          minWidth: "150",
        },
        {
          prop: "type",
          label: this.$t("dns.record.type"),
          minWidth: "100",
        },
        {
          prop: "content",
          label: this.$t("dns.record.value"),
          minWidth: "300",
        },
        {
          prop: "prio",
          label: this.$t("dns.record.weght"),
          minWidth: "80",
        },
        {
          prop: "ttl",
          label: this.$t("dns.record.ttl"),
          minWidth: "100",
        },
        {
          prop: "change_date",
          label: this.$t("dns.record.change_date"),
          minWidth: "180",
        },
      ],
      recordtype: [],
      pagesizes: PAGE_OPTIONS,
      pagesize: 10,
      currentpage: 1,
      searchatext: "",
      recordlist: [],
      // 表格显示控制
      is_edit: true,
      is_delete: true,
      selection: false, // 默认不显示复选框
      radio: false,
      space_color: true, // 默认启用隔行变色
    };
  },
  computed: {
    /**
     * 搜索过滤后的记录列表
     */
    searchrecord() {
      if (!this.searchatext || !this.searchatext.trim()) {
        return this.recordlist;
      }
      
      const searchText = this.searchatext.trim().toLowerCase();
      return this.recordlist.filter(record => {
        const name = (record.name || '').toLowerCase();
        const content = (record.content || '').toLowerCase();
        return name.includes(searchText) || content.includes(searchText);
      });
    },
    
    /**
     * 当前页显示的记录列表（优化：使用computed自动计算）
     */
    tempList() {
      const start = (this.currentpage - 1) * this.pagesize;
      const end = start + this.pagesize;
      return this.searchrecord.slice(start, end);
    },
    
    /**
     * 总记录数
     */
    recordtotal() {
      return this.searchrecord.length;
    },
  },
  mounted() {
    this.initPage();
    this.domain = this.$route.params.domain;
    this.getrecordtype();
  },
  methods: {
    /**
     * 初始化页面
     */
    initPage() {
      this.title = this.$t("menuitems.dns.record");
      this.items = [
        {
          text: this.$t("menuitems.dns.text"),
          href: "/",
        },
        {
          text: this.$t("menuitems.dns.list"),
          href: "/dns/list",
        },
        {
          text: this.$route.params.domain + " " + this.$t("menuitems.dns.record"),
          active: true,
        },
      ];
    },
    
    /**
     * 获取记录类型
     */
    async getrecordtype() {
      if (!this.checkAuth()) {
        return;
      }
      
      try {
        const response = await apiGet(API_ENDPOINTS.RECORD_TYPES, {}, {
          vm: this,
          showLoading: false
        });
        
        if (response && response.status === 200) {
          this.recordtype = response.data || [];
          await this.getdnsrecord();
        }
      } catch (error) {
        console.error('加载记录类型失败:', error);
        // 401错误已在adapter.js中处理
        if (error.response && error.response.status !== 401) {
          this.$message({
            message: '加载记录类型失败',
            type: 'error',
          });
        }
      }
    },
    
    /**
     * 获取DNS记录列表
     */
    async getdnsrecord() {
      if (!this.checkAuth() || !this.domain) {
        return;
      }
      
      try {
        const response = await apiGet(API_ENDPOINTS.RECORDS, {
          params: {
            domain: this.domain,
          }
        }, {
          vm: this,
          showLoading: true,
          loadingText: '加载DNS记录...'
        });
        
        if (response && response.status === 200) {
          this.loading = false;
          this.recordlist = response.data || [];
          this.recordtype = response.recordtype || this.recordtype;
          
          // 重置到第一页
          if (this.currentpage > 1) {
            this.currentpage = 1;
          }
        }
      } catch (error) {
        console.error('加载DNS记录失败:', error);
        this.loading = false;
        // 401错误已在adapter.js中处理
        if (error.response && error.response.status !== 401) {
          this.$message({
            message: '加载DNS记录失败',
            type: 'error',
          });
        }
      }
    },
    
    /**
     * 处理搜索输入
     */
    handleSearchInput() {
      // 搜索后重置到第一页
      if (this.currentpage > 1) {
        this.currentpage = 1;
      }
    },
    
    /**
     * 处理分页大小变化
     */
    handleSizeChange(val) {
      this.pagesize = val;
      // 重新计算当前页（避免超出范围）
      const maxPage = Math.ceil(this.recordtotal / this.pagesize);
      if (this.currentpage > maxPage) {
        this.currentpage = maxPage || 1;
      }
    },
    
    /**
     * 处理当前页变化
     */
    handleCurrentChange(val) {
      this.currentpage = val;
    },
    
    /**
     * 表格行样式（使用Vue的方式而不是直接操作DOM）
     */
    tableRowClassName({ row, rowIndex }) {
      if (this.space_color && rowIndex % 2 === 0) {
        return 'table-row-even';
      }
      return '';
    },
    
    /**
     * 编辑记录
     */
    handleEdit(index, row) {
      row.edit = true;
    },
    
    /**
     * 取消编辑
     */
    handleCancel(index, row) {
      // 如果是新增的记录，直接删除
      if (row.add) {
        const indexInList = this.recordlist.findIndex(r => r === row);
        if (indexInList !== -1) {
          this.recordlist.splice(indexInList, 1);
        }
      } else {
        row.edit = false;
        // 重新获取数据以恢复原值
        this.getdnsrecord();
      }
    },
    
    /**
     * 保存记录
     */
    async handleSave(index, row) {
      if (!this.checkAuth()) {
        return;
      }
      
      const isEdit = !!row.id;
      const endpoint = isEdit ? API_ENDPOINTS.EDIT_RECORD : API_ENDPOINTS.ADD_RECORD;
      
      const data = {
        ...row,
      };
      
      if (!isEdit) {
        data.domain = this.domain;
      }
      
      row.loading = true;
      
      try {
        const response = await apiPost(endpoint, data, {}, {
          vm: this,
          showSuccess: true,
          successMessage: isEdit 
            ? this.$t("global.tips.alter.update.success")
            : this.$t("global.tips.alter.save.success"),
          showLoading: false
        });
        
        if (response && response.status === 200) {
          row.edit = false;
          row.loading = false;
          
          if (!isEdit) {
            // 新增记录后重新获取列表
            await this.getdnsrecord();
          } else {
            // 编辑记录后刷新列表
            await this.getdnsrecord();
          }
        }
      } catch (error) {
        console.error('保存记录失败:', error);
        row.loading = false;
        // 错误已在handleApiResponse中处理
      }
    },
    
    /**
     * 删除记录
     */
    async handleDelete(index, row) {
      if (!this.checkAuth()) {
        return;
      }
      
      try {
        await this.$confirm(
          this.$t("waring.tips.delete"),
          this.$t("waring.tips.text"),
          {
            confirmButtonText: this.$t("alter.button.ok"),
            cancelButtonText: this.$t("alter.button.cancel"),
            type: "warning",
          }
        );
        
        try {
          await apiPost(API_ENDPOINTS.DELETE_RECORD, { id: row.id }, {}, {
            vm: this,
            showSuccess: true,
            successMessage: this.$t("global.tips.alter.delete.success"),
            showLoading: true
          });
          
          // 重新获取列表
          await this.getdnsrecord();
        } catch (error) {
          // 错误已在handleApiResponse中处理
          if (error.response && error.response.status === 403) {
            this.$message({
              message: this.$t("global.tips.alter.auth.needauth"),
              type: "warning",
            });
          }
        }
      } catch (error) {
        // 用户取消操作
        if (error !== 'cancel') {
          console.error('删除记录失败:', error);
        }
      }
    },
    
    /**
     * 启用/禁用记录
     */
    async handleLock(index, row) {
      if (!this.checkAuth()) {
        return;
      }
      
      const data = {
        id: row.id,
        disable: row.disabled === 0 ? 1 : 0,
      };
      
      row.loading = true;
      
      try {
        const response = await apiPost(API_ENDPOINTS.DISABLE_RECORD, data, {}, {
          vm: this,
          showSuccess: true,
          successMessage: row.disabled === 0
            ? this.$t("global.tips.alter.disabled.success")
            : this.$t("global.tips.alter.disabled.unlock"),
          showLoading: false
        });
        
        if (response && response.status === 200) {
          row.disabled = row.disabled === 0 ? 1 : 0;
          row.loading = false;
        }
      } catch (error) {
        console.error('启用/禁用记录失败:', error);
        row.loading = false;
        if (error.response && error.response.status === 403) {
          this.$message({
            message: this.$t("global.tips.alter.auth.needauth"),
            type: "warning",
          });
        }
      }
    },
    
    /**
     * 添加记录
     */
    handleAdd() {
      const addDataJson = {
        name: "",
        type: "A",
        content: "",
        disabled: 0,
        domain_id: this.recordlist[0]?.domain_id || null,
        loading: false,
        ttl: 600,
        prio: 0,
        vft: true,
        edit: true,
        add: true,
      };
      
      this.recordlist.unshift(addDataJson);
      
      // 重置到第一页显示新增记录
      this.currentpage = 1;
    },
    
    /**
     * 跳转到DNSSEC页面
     */
    dnssec() {
      this.$router.push({
        path: `/dns/${this.domain}/record/dnssec`,
      });
    },
    
    /**
     * 获取记录类型标签类型（用于el-tag的颜色）
     */
    getRecordTypeTagType(type) {
      const typeMap = {
        'A': 'success',
        'AAAA': 'primary',
        'CNAME': 'warning',
        'MX': 'danger',
        'TXT': 'info',
        'NS': '',
        'PTR': 'success',
        'SRV': 'warning',
        'CAA': 'info',
      };
      return typeMap[type] || '';
    },
  },
  filters: {
    /**
     * 格式化时间
     */
    /**
     * 日期排序方法
     */
    sortByDate(a, b) {
      const dateA = a.change_date ? new Date(a.change_date).getTime() : 0;
      const dateB = b.change_date ? new Date(b.change_date).getTime() : 0;
      return dateB - dateA; // 降序排列
    },
    
    /**
     * 格式化时间
     */
    formatTimer(value) {
      if (!value) return '';
      
      // 如果value已经是格式化的日期时间字符串，直接返回
      if (typeof value === 'string' && value.includes('-') && value.includes(':')) {
        return value;
      }
      
      // 如果value是时间戳（数字或数字字符串），转换为Date对象
      let timestamp;
      if (typeof value === 'number') {
        // 如果是秒级时间戳，转换为毫秒
        timestamp = value < 10000000000 ? value * 1000 : value;
      } else if (typeof value === 'string') {
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue)) {
          timestamp = numValue < 10000000000 ? numValue * 1000 : numValue;
        } else {
          // 尝试解析日期字符串
          timestamp = new Date(value).getTime();
        }
      } else {
        return '';
      }
      
      if (isNaN(timestamp)) {
        return value;
      }
      
      return formatDateTime(timestamp);
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body" v-loading="loading">
            <!-- 操作按钮 -->
            <div class="mb-3">
              <el-button 
                size="mini" 
                type="primary" 
                icon="el-icon-plus"
                @click="handleAdd"
              >
                {{ $t("dns.record.add") }}
              </el-button>
              <el-button 
                size="mini" 
                type="success" 
                icon="el-icon-lock"
                @click="dnssec"
              >
                {{ $t("dns.record.dnssec") }}
              </el-button>
            </div>
            
            <!-- 搜索框 -->
            <div class="mb-3">
              <el-input
                v-model="searchatext"
                size="small"
                :placeholder="$t('dns.record.search')"
                prefix-icon="el-icon-search"
                clearable
                @input="handleSearchInput"
                style="max-width: 300px;"
              ></el-input>
            </div>

            <!-- 数据表格 - Element UI 表格 -->
            <el-table
              ref="mytable"
              :data="tempList"
              :row-class-name="tableRowClassName"
              style="width: 100%"
              stripe
              border
              :fit="true"
              :default-sort="{prop: 'change_date', order: 'descending'}"
              highlight-current-row
              :empty-text="$t('dns.record.no_data') || '暂无数据'"
              :max-height="ismobile ? 'auto' : undefined"
              tooltip-effect="dark"
            >
              <el-table-column
                v-if="radio"
                type="index"
                width="50"
                label="#"
              ></el-table-column>
              
              <el-table-column
                v-if="selection"
                type="selection"
                width="50"
              ></el-table-column>
              
              <!-- 动态列 - Element UI 表格列 -->
              <el-table-column
                v-for="(item, index) in table_columns"
                :key="item.prop"
                :label="item.label"
                :prop="item.prop"
                :width="item.width"
                :min-width="item.minWidth || 100"
                :show-overflow-tooltip="true"
                :column-key="item.prop"
                :class-name="`table-column-${item.prop}`"
                :sortable="item.sortable !== false && item.prop !== 'content'"
                :sort-method="item.prop === 'change_date' ? sortByDate : undefined"
                align="center"
              >
                <template slot-scope="scope">
                  <!-- 编辑模式：主机名 -->
                  <el-input
                    v-if="scope.row.edit && item.prop === 'name'"
                    size="small"
                    v-model="scope.row[item.prop]"
                    :placeholder="'请输入' + item.label"
                  ></el-input>
                  
                  <!-- 编辑模式：记录值 -->
                  <el-input
                    v-else-if="scope.row.edit && item.prop === 'content'"
                    size="small"
                    v-model="scope.row[item.prop]"
                    :placeholder="'请输入' + item.label"
                  ></el-input>
                  
                  <!-- 编辑模式：TTL -->
                  <el-input-number
                    v-else-if="scope.row.edit && item.prop === 'ttl'"
                    v-model="scope.row[item.prop]"
                    :min="60"
                    :max="86400"
                    size="small"
                    style="width: 100%"
                  ></el-input-number>
                  
                  <!-- 编辑模式：优先级（仅MX记录） -->
                  <el-input-number
                    v-else-if="scope.row.edit && item.prop === 'prio' && scope.row.type === 'MX'"
                    v-model="scope.row[item.prop]"
                    :min="0"
                    :max="65535"
                    size="small"
                    style="width: 100%"
                  ></el-input-number>
                  
                  <!-- 编辑模式：优先级（非MX记录，禁用） -->
                  <el-input-number
                    v-else-if="scope.row.edit && item.prop === 'prio' && scope.row.type !== 'MX'"
                    v-model="scope.row[item.prop]"
                    disabled
                    size="small"
                    style="width: 100%"
                  ></el-input-number>

                  <!-- 编辑模式：记录类型 -->
                  <el-select
                    v-else-if="scope.row.edit && item.prop === 'type'"
                    v-model="scope.row[item.prop]"
                    filterable
                    size="small"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="type in recordtype"
                      :key="type"
                      :label="type"
                      :value="type"
                    ></el-option>
                  </el-select>
                  
                  <!-- 显示模式：记录类型（带颜色标签） -->
                  <el-tag
                    v-else-if="!scope.row.edit && item.prop === 'type'"
                    :type="getRecordTypeTagType(scope.row[item.prop])"
                    size="small"
                    effect="plain"
                  >
                    {{ scope.row[item.prop] }}
                  </el-tag>
                  
                  <!-- 显示模式：时间格式化 -->
                  <span 
                    v-else-if="!scope.row.edit && item.prop === 'change_date'"
                    :data-time="true"
                    :title="scope.row[item.prop] | formatTimer"
                  >
                    {{ scope.row[item.prop] | formatTimer }}
                  </span>
                  
                  <!-- 显示模式：内容值（长文本显示优化，使用 tooltip） -->
                  <span 
                    v-else-if="!scope.row.edit && item.prop === 'content'"
                    class="content-cell"
                    :title="scope.row[item.prop]"
                  >
                    {{ scope.row[item.prop] || '-' }}
                  </span>
                  
                  <!-- 显示模式：主机名 -->
                  <span 
                    v-else-if="!scope.row.edit && item.prop === 'name'"
                    :title="scope.row[item.prop] || '@'"
                    style="font-weight: 500; color: #303133;"
                  >
                    {{ scope.row[item.prop] || '@' }}
                  </span>
                  
                  <!-- 显示模式：优先级（仅MX记录显示，其他显示-） -->
                  <span 
                    v-else-if="!scope.row.edit && item.prop === 'prio'"
                    :style="{ color: scope.row.type === 'MX' ? '#303133' : '#c0c4cc' }"
                  >
                    {{ scope.row.type === 'MX' ? (scope.row[item.prop] || 0) : '-' }}
                  </span>
                  
                  <!-- 显示模式：TTL（带单位） -->
                  <span 
                    v-else-if="!scope.row.edit && item.prop === 'ttl'"
                    style="font-family: monospace; color: #606266;"
                  >
                    {{ scope.row[item.prop] || '-' }}
                  </span>
                  
                  <!-- 显示模式：其他字段 -->
                  <span v-else-if="!scope.row.edit">
                    {{ scope.row[item.prop] || '-' }}
                  </span>
                </template>
              </el-table-column>
              
              <!-- 操作列 -->
              <el-table-column 
                :label="$t('dns.record.action')" 
                align="center"
                min-width="220"
                width="220"
                fixed="right"
              >
                <template slot-scope="scope">
                  <!-- 编辑/保存/取消按钮（已有记录且可编辑） -->
                  <template v-if="is_edit && !scope.row.add && scope.row.vft">
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="$t('dns.list.button.edit')"
                      placement="top"
                    >
                      <el-button
                        type="primary"
                        icon="el-icon-edit"
                        size="mini"
                        circle
                        v-if="!scope.row.edit"
                        @click="handleEdit(scope.$index, scope.row)"
                      ></el-button>
                    </el-tooltip>
                    
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="$t('dns.list.button.save')"
                      placement="top"
                    >
                      <el-button
                        type="success"
                        icon="el-icon-check"
                        size="mini"
                        circle
                        v-if="scope.row.edit"
                        :loading="scope.row.loading"
                        @click="handleSave(scope.$index, scope.row)"
                      ></el-button>
                    </el-tooltip>
                    
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="$t('dns.list.button.cancel')"
                      placement="top"
                    >
                      <el-button
                        type="danger"
                        icon="el-icon-close"
                        size="mini"
                        circle
                        v-if="scope.row.edit"
                        @click="handleCancel(scope.$index, scope.row)"
                      ></el-button>
                    </el-tooltip>
                  </template>
                  
                  <!-- 保存/取消按钮（新增记录） -->
                  <template v-if="scope.row.add && scope.row.vft">
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="$t('dns.list.button.save')"
                      placement="top"
                    >
                      <el-button
                        type="success"
                        icon="el-icon-check"
                        size="mini"
                        circle
                        :loading="scope.row.loading"
                        @click="handleSave(scope.$index, scope.row)"
                      ></el-button>
                    </el-tooltip>
                    
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="$t('dns.list.button.cancel')"
                      placement="top"
                    >
                      <el-button
                        type="danger"
                        icon="el-icon-close"
                        size="mini"
                        circle
                        @click="handleCancel(scope.$index, scope.row)"
                      ></el-button>
                    </el-tooltip>
                  </template>
                  
                  <!-- 启用/禁用按钮 -->
                  <template v-if="scope.row.vft && !scope.row.add">
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="$t('dns.list.button.suspend')"
                      placement="top"
                    >
                      <el-button
                        type="warning"
                        icon="el-icon-video-pause"
                        size="mini"
                        circle
                        :loading="scope.row.loading"
                        v-if="scope.row.disabled === 0"
                        @click="handleLock(scope.$index, scope.row)"
                      ></el-button>
                    </el-tooltip>
                    
                    <el-tooltip
                      class="item"
                      effect="dark"
                      :content="$t('dns.list.button.unsuspend')"
                      placement="top"
                    >
                      <el-button
                        type="info"
                        icon="el-icon-video-play"
                        size="mini"
                        circle
                        :loading="scope.row.loading"
                        v-if="scope.row.disabled === 1"
                        @click="handleLock(scope.$index, scope.row)"
                      ></el-button>
                    </el-tooltip>
                  </template>
                  
                  <!-- 删除按钮 -->
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="$t('dns.list.button.delete')"
                    placement="top"
                    v-if="is_delete && scope.row.vft && !scope.row.add"
                  >
                    <el-button
                      type="danger"
                      icon="el-icon-delete"
                      size="mini"
                      circle
                      @click="handleDelete(scope.$index, scope.row)"
                    ></el-button>
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
            
            <!-- 分页 -->
            <div class="text-center mt-3">
              <el-pagination
                background
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page.sync="currentpage"
                :page-sizes="pagesizes"
                :page-size.sync="pagesize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="recordtotal"
              >
              </el-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
/* ==================== 表格基础样式 ==================== */
::v-deep .el-table {
  font-size: 14px;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  table-layout: auto; /* 使用自动布局，让列可以扩展 */
}

/* 确保表格容器占满宽度并自动扩展 */
::v-deep .el-table__body-wrapper,
::v-deep .el-table__header-wrapper {
  width: 100% !important;
}

/* 让表格列可以自动扩展填充空间 */
::v-deep .el-table .el-table__header-wrapper table,
::v-deep .el-table .el-table__body-wrapper table {
  width: 100%;
  table-layout: auto;
}

/* 高分辨率下优化列宽分配 */
@media (min-width: 1400px) {
  ::v-deep .el-table .el-table-column {
    /* 让内容列占据更多空间 */
  }
  
  ::v-deep .el-table .el-table-column[data-prop="content"] {
    min-width: 350px !important;
  }
}

/* 表头样式 */
::v-deep .el-table th {
  background: linear-gradient(180deg, #f5f7fa 0%, #eef0f3 100%);
  font-weight: 600;
  color: #303133;
  padding: 12px 0;
  border-bottom: 2px solid #e4e7ed;
}

/* 表头单元格 */
::v-deep .el-table th > .cell {
  font-weight: 600;
  color: #303133;
}

/* 表格行样式 */
::v-deep .el-table td {
  padding: 12px 0;
  border-bottom: 1px solid #f0f2f5;
}

/* 隔行变色样式 - 优化颜色 */
::v-deep .table-row-even {
  background-color: #f8f9fa;
}

::v-deep .el-table .table-row-even:hover {
  background-color: #f0f9ff !important;
}

/* 普通行hover效果 */
::v-deep .el-table tbody tr:hover > td {
  background-color: #f5f9fc !important;
}

/* 编辑行高亮 */
::v-deep .el-table tbody tr[class*="table-row"]:has(.el-input),
::v-deep .el-table tbody tr[class*="table-row"]:has(.el-select),
::v-deep .el-table tbody tr[class*="table-row"]:has(.el-input-number) {
  background-color: #fef5e7 !important;
}

/* ==================== 单元格内容优化 ==================== */
/* 单元格基础样式 */
::v-deep .cell {
  padding: 0 8px;
  line-height: 1.6;
  word-break: break-word;
}

/* 内容溢出处理 - 显示省略号，hover时显示tooltip */
::v-deep .cell span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* 长文本内容优化 */
::v-deep .cell[title] {
  cursor: help;
}

/* 内容值为空时的显示 */
::v-deep .cell span:empty::before {
  content: '-';
  color: #c0c4cc;
}

/* ==================== 编辑模式样式 ==================== */
/* 编辑输入框样式 */
::v-deep .el-table .el-input__inner,
::v-deep .el-table .el-input-number__input {
  border-radius: 4px;
  transition: all 0.3s;
}

::v-deep .el-table .el-input__inner:focus,
::v-deep .el-table .el-input-number__input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

/* 编辑模式下的下拉框 */
::v-deep .el-table .el-select {
  width: 100%;
}

::v-deep .el-table .el-select .el-input__inner {
  border-radius: 4px;
}

/* 数字输入框样式 */
::v-deep .el-table .el-input-number {
  width: 100%;
}

::v-deep .el-table .el-input-number.is-disabled .el-input__inner {
  background-color: #f5f7fa;
  color: #909399;
}

/* ==================== 操作按钮优化 ==================== */
/* 按钮组容器 */
::v-deep .el-table .cell {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
}

/* 按钮样式优化 */
::v-deep .el-table .el-button--mini {
  padding: 7px 9px;
  border-radius: 4px;
  transition: all 0.3s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

::v-deep .el-table .el-button--mini:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

::v-deep .el-table .el-button--mini.is-circle {
  padding: 7px;
  width: 28px;
  height: 28px;
}

/* 按钮图标大小 */
::v-deep .el-table .el-button--mini .el-icon {
  font-size: 14px;
}

/* 加载状态按钮 */
::v-deep .el-table .el-button.is-loading {
  pointer-events: none;
  opacity: 0.7;
}

/* 禁用状态记录样式 */
::v-deep .el-table tbody tr:has([data-disabled="1"]) {
  opacity: 0.6;
}

::v-deep .el-table tbody tr:has([data-disabled="1"]) .cell {
  color: #909399;
  text-decoration: line-through;
}

/* ==================== 类型标签样式 ==================== */
/* 记录类型显示优化 */
::v-deep .cell[data-type] {
  font-weight: 500;
}

::v-deep .cell[data-type="A"] {
  color: #67c23a;
}

::v-deep .cell[data-type="AAAA"] {
  color: #409eff;
}

::v-deep .cell[data-type="CNAME"] {
  color: #e6a23c;
}

::v-deep .cell[data-type="MX"] {
  color: #f56c6c;
}

::v-deep .cell[data-type="TXT"] {
  color: #909399;
}

/* ==================== 时间显示优化 ==================== */
::v-deep .cell[data-time] {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  color: #606266;
}

/* ==================== 内容列优化 ==================== */
::v-deep .content-cell {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-all;
}

/* ==================== 高分辨率优化 ==================== */
/* 让表格在高分辨率下充分利用空间，通过增加列的最小宽度 */

/* 1400px 及以上 */
@media (min-width: 1400px) {
  ::v-deep .el-table {
    font-size: 15px;
  }
  
  /* 通过类名定位列并增加最小宽度 */
  ::v-deep .el-table .table-column-content {
    min-width: 400px !important;
  }
  
  ::v-deep .el-table .table-column-name {
    min-width: 200px !important;
  }
}

/* 1600px 及以上 - 进一步增加列宽 */
@media (min-width: 1600px) {
  ::v-deep .el-table .table-column-content {
    min-width: 450px !important;
  }
  
  ::v-deep .el-table .table-column-name {
    min-width: 220px !important;
  }
}

/* 1920px 及以上 - 全高清分辨率优化 */
@media (min-width: 1920px) {
  ::v-deep .el-table .table-column-content {
    min-width: 550px !important;
  }
  
  ::v-deep .el-table .table-column-name {
    min-width: 240px !important;
  }
  
  ::v-deep .el-table .table-column-change_date {
    min-width: 200px !important;
  }
}

/* 2560px 及以上 - 4K 分辨率优化 */
@media (min-width: 2560px) {
  ::v-deep .el-table {
    font-size: 16px;
  }
  
  ::v-deep .el-table .table-column-content {
    min-width: 700px !important;
  }
  
  ::v-deep .el-table .table-column-name {
    min-width: 280px !important;
  }
  
  ::v-deep .el-table .table-column-change_date {
    min-width: 240px !important;
  }
}

/* ==================== 响应式优化 ==================== */
@media (max-width: 1200px) {
  ::v-deep .el-table {
    font-size: 13px;
  }
  
  ::v-deep .el-table th,
  ::v-deep .el-table td {
    padding: 10px 0;
  }
}

@media (max-width: 768px) {
  ::v-deep .el-table {
    font-size: 12px;
  }
  
  ::v-deep .el-table th,
  ::v-deep .el-table td {
    padding: 8px 0;
  }
  
  ::v-deep .el-button--mini {
    padding: 5px 7px;
    font-size: 12px;
  }
  
  ::v-deep .el-button--mini.is-circle {
    padding: 5px;
    width: 24px;
    height: 24px;
  }
  
  ::v-deep .el-table .el-button--mini .el-icon {
    font-size: 12px;
  }
  
  /* 移动端隐藏操作列tooltip */
  ::v-deep .el-table .el-tooltip {
    display: none;
  }
}

@media (max-width: 480px) {
  ::v-deep .el-table {
    font-size: 11px;
  }
  
  ::v-deep .el-table .cell {
    padding: 0 4px;
  }
  
  ::v-deep .el-button--mini {
    padding: 4px 6px;
    font-size: 11px;
  }
}

/* ==================== 动画效果 ==================== */
/* 表格行过渡动画 */
::v-deep .el-table tbody tr {
  transition: background-color 0.3s ease;
}

/* 按钮点击动画 */
::v-deep .el-table .el-button--mini:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* ==================== 滚动条优化 ==================== */
::v-deep .el-table__body-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::v-deep .el-table__body-wrapper::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 4px;
}

::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}

::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8abb2;
}

/* ==================== 空状态优化 ==================== */
::v-deep .el-table__empty-block {
  padding: 40px 0;
}

::v-deep .el-table__empty-text {
  color: #909399;
  font-size: 14px;
}
</style>
